import { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { registerLocaleData } from '@angular/common';
import { DynamicDocumentCellComponent } from './cell-components/dynamic-document-cell/dynamic-document-cell.component';
import localeData from '@angular/common/locales/en';
import { camelCaseFormatter } from 'app/components/grid-components/grid-formatters';
import { dateFilterComparator } from 'app/components/grid-components/grid-comparitors';
import { formatDistanceToNow } from 'date-fns';
import { FileNameCellComponent } from './cell-components/filename-cell/filename-cell.component';

registerLocaleData(localeData);
const contactFormatter = (contact: { first: string, last: string, email: string }) => {
    const first = contact?.first.charAt(0).toUpperCase() + contact?.first.slice(1).toLowerCase();
    const last = contact?.last.charAt(0).toUpperCase() + contact?.last.slice(1).toLowerCase();
  
    return `${first} ${last}`;
};

export const formatCategory = (value: string) => {
  if (value?.match('^pL')) {
    const _formattedValue = camelCaseFormatter({ value: value.slice(2) });
    return `P&L ${_formattedValue.charAt(0).toLowerCase()}${_formattedValue.slice(1)}`;
  } else {
    return camelCaseFormatter({ value });
  }
};

export const COL_DEF_FILE_NAME: ColDef = {
  headerName: 'File name',
  headerClass: 'filename-header-cell',
  field: 'filename',
  colId: 'filename',
  filter: 'agTextColumnFilter',
  cellRenderer: FileNameCellComponent,
  headerCheckboxSelection: true,
  filterParams: {
    filterOptions: ['startsWith', 'contains'],
  },
  comparator: (valueA, valueB) => {
    const nameA: string = valueA.trim();
    const nameB: string = valueB.trim();
    return nameA?.localeCompare(nameB);
  }
};

export const COL_DEF_TYPE: ColDef = {
  headerName: 'Type',
  field: 'category',
  colId: 'category',
  filter: 'agSetColumnFilter',
  cellClassRules: {
    'tw-text-lendio-cool-gray-400': params => params.data.scanStatus !== 'CLEAN'
  },
  valueFormatter: ({ value, data }: ValueFormatterParams) => {
    const formattedValue = formatCategory(value);

    const dateValue = data.months && data.months.length ? ` ${data.months[0]}` : (
      data.years && data.years.length ? ` ${data.years[0]}` : ''
    );

    return  `${formattedValue}${dateValue}`;
      
  },
  filterValueGetter: (params: ValueGetterParams) => {
    return formatCategory(params.data.category);
  },
  tooltipValueGetter: ({ data, value }) => {
    // Show the original category value in tooltip
    const formattedValue = formatCategory(value);

    const dateValue = data.months && data.months.length ? ` ${data.months[0]}` : (
      data.years && data.years.length ? ` ${data.years[0]}` : ''
    );

    return  `${formattedValue}${dateValue}`;
  },
  comparator: (valueA, valueB) => {
    const typeA: string = valueA.trim();
    const typeB: string = valueB.trim();
    return typeA?.localeCompare(typeB);
  },
};


export const COL_DEF_UPLOADED: ColDef = {
  headerName: 'Uploaded on',
  hide: true,
  field: 'created',
  colId: 'created',
  filter: 'agDateColumnFilter',
  filterParams: {
    maxValidDate: new Date().toISOString().slice(0, 10),
    comparator: dateFilterComparator,
    filterOptions: [
      'lessThan',
      'greaterThan',
      'inRange'
    ],
    sort: 'desc',
    defaultOption: 'lessThan',
    inRangeInclusive: true,
  },
  cellClassRules: {
    'tw-text-lendio-cool-gray-400': params => params.data.scanStatus !== 'CLEAN'
  },
  sortingOrder: ['asc', 'desc', null],
  valueGetter: (params: ValueGetterParams) => params.data.createdTimestamp ?? params.data.created,
  valueFormatter: ({ value }: ValueFormatterParams) => value
      ? `${formatDistanceToNow(new Date(value * 1000))} ago`
      : '',
  comparator: (valueA, valueB) => valueA - valueB
};

export const COL_DEF_DYNAMIC_ACTIONS: ColDef = {
  hide: false,
  headerName: 'Review/Reject',
  field: 'status',
  colId: 'status',
  cellRenderer: DynamicDocumentCellComponent,
  filter: 'agSetColumnFilter',
  valueGetter: (params: ValueGetterParams) => params.data.status?.reviewed && !params.data.status?.rejected ? 'Reviewed' : 
      (params.data.status?.rejected ? 'Rejected' : 'Pending'),
  sortable: true,
  cellStyle: { 
    display: 'flex',
    justifyContent: 'start'
  },
};

export const COL_DEF_DYNAMIC_BORROWER_ACTIONS: ColDef = {
  hide: false,
  headerName: 'Actions',
  field: 'actions',
  colId: 'actions',
  cellRenderer: DynamicDocumentCellComponent,
  sortable: false,
  valueGetter: (params: ValueGetterParams) => null,
  cellStyle: { 
    display: 'flex',
    justifyContent: 'start'
  },
};

export const COL_DEF_SCAN_STATUS: ColDef = {
  hide: true,
  headerName: 'Scan',
  field: 'scanStatus',
  colId: 'scanStatus',
  filter: 'agSetColumnFilter',
  comparator: (valueA, valueB) => {
    const nameA: string = valueA.trim();
    const nameB: string = valueB.trim();
    return nameA?.localeCompare(nameB);
  }
};

export const COL_DEF_CONTACT: ColDef = {
  hide: false,
  headerName: 'Contact',
  field: 'contact',
  colId: 'contact',
  cellClassRules: {
    'tw-text-gray-400': params => params.data.scanStatus !== 'CLEAN'
  },
  filter: 'agTextColumnFilter',
  valueGetter: (params: ValueGetterParams) => params.data.contact ? contactFormatter(params.data.contact) : '',
  sortable: true,
  cellStyle: { 
    display: 'flex',
    justifyContent: 'start'
  }
};

export const COL_DEF_UPLOADED_BY: ColDef = {
  hide: false,
  headerName: 'Uploaded by',
  field: 'uploadedBy',
  colId: 'uploadedBy',
  cellClassRules: {
    'tw-text-gray-400': params => params.data.scanStatus !== 'CLEAN'
  },
  filter: 'agTextColumnFilter',
  valueGetter: (params: ValueGetterParams) => {
    if (params.data.uploadedBy === 'system') {
      return 'System Account';
    } else if (params.data.uploadedBy === 'lenderUser' && params.data.lenderUser) {
      return params.data.lenderUser.fullName;
    } else if (params.data.uploadedBy === 'borrower' && params.data.borrower?.primary_contact) {
      return `${params.data.borrower.primary_contact.first} ${params.data.borrower.primary_contact.last}`;
    }
    return 'Unknown';
  },
  sortable: true,
};

export const COL_DEF_REVIEWED_ON: ColDef = {
  hide: false,
  headerName: 'Reviewed on',
  field: 'reviewedOn',
  colId: 'reviewedOn',
  filter: 'agDateColumnFilter',
  cellClassRules: {
    'tw-text-gray-400': params => params.data.scanStatus !== 'CLEAN'
  },
  valueGetter: (params: ValueGetterParams) => params.data.status?.reviewedAt || null,
  valueFormatter: ({ value }: ValueFormatterParams) => value ? formatDistanceToNow(new Date(value * 1000)) : '',
  sortable: true,
};

export const COL_DEF_REVIEWED_BY: ColDef = {
  hide: false,
  headerName: 'Reviewed by',
  field: 'reviewedBy',
  colId: 'reviewedBy',
  cellClassRules: {
    'tw-text-gray-400': params => params.data.scanStatus !== 'CLEAN'
  },
  filter: 'agTextColumnFilter',
  valueGetter: (params: ValueGetterParams) => params.data.status?.reviewedBy?.fullName || '',
  sortable: true,
};

export const COL_DEF_REVIEW_STATUS: ColDef = {
  hide: false,
  headerName: 'Review Status',
  field: 'reviewStatus',
  colId: 'reviewStatus',
  filter: 'agSetColumnFilter',
  cellClassRules: {
    'tw-text-lendio-red-500': params => params.data.status?.rejected,
    'tw-text-lendio-green-500': params => params.data.status?.reviewed && !params.data.status?.rejected,
    'tw-text-lendio-cool-gray-400': params => params.data.scanStatus !== 'CLEAN'
  },
  valueGetter: (params: ValueGetterParams) => {
    if (params.data.status?.rejected) {
      return 'Rejected';
    } else if (params.data.status?.reviewed && !params.data.status?.rejected) {
      return 'Accepted';
    }
    return 'Pending';
  },
  sortable: true,
};
